import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  Snackbar,
  Alert,
  AlertTitle,
  Button,
  Box,
  Slide,
  SlideProps
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { notificationService, Notification } from '../services/notificationService';

interface NotificationContextType {
  notifications: Notification[];
  removeNotification: (id: string) => void;
  clearAll: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="left" />;
}

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  useEffect(() => {
    const unsubscribe = notificationService.subscribe(setNotifications);
    return unsubscribe;
  }, []);

  const removeNotification = (id: string) => {
    notificationService.remove(id);
  };

  const clearAll = () => {
    notificationService.clear();
  };

  const contextValue: NotificationContextType = {
    notifications,
    removeNotification,
    clearAll
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      <NotificationContainer />
    </NotificationContext.Provider>
  );
};

const NotificationContainer: React.FC = () => {
  const { notifications, removeNotification } = useNotifications();
  const [currentNotification, setCurrentNotification] = useState<Notification | null>(null);

  useEffect(() => {
    if (notifications.length > 0 && !currentNotification) {
      setCurrentNotification(notifications[0]);
    }
  }, [notifications, currentNotification]);

  const handleClose = () => {
    if (currentNotification) {
      removeNotification(currentNotification.id);
      setCurrentNotification(null);
    }
  };

  const handleExited = () => {
    // Show next notification if any
    const remainingNotifications = notifications.filter(n => n.id !== currentNotification?.id);
    if (remainingNotifications.length > 0) {
      setCurrentNotification(remainingNotifications[0]);
    }
  };

  if (!currentNotification) {
    return null;
  }

  return (
    <Snackbar
      open={true}
      autoHideDuration={currentNotification.persistent ? null : (currentNotification.duration || 4000)}
      onClose={currentNotification.persistent ? undefined : handleClose}
      TransitionComponent={SlideTransition}
      TransitionProps={{
        onExited: handleExited
      } as TransitionProps}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      sx={{ mt: 8 }}
    >
      <Alert
        severity={currentNotification.type}
        onClose={currentNotification.persistent ? undefined : handleClose}
        sx={{ minWidth: 300, maxWidth: 500 }}
        action={
          currentNotification.actions && currentNotification.actions.length > 0 ? (
            <Box display="flex" gap={1}>
              {currentNotification.actions.map((action, index) => (
                <Button
                  key={index}
                  color="inherit"
                  size="small"
                  variant={action.primary ? 'contained' : 'text'}
                  onClick={() => {
                    action.action();
                    if (!currentNotification.persistent) {
                      handleClose();
                    }
                  }}
                >
                  {action.label}
                </Button>
              ))}
            </Box>
          ) : undefined
        }
      >
        {currentNotification.title && (
          <AlertTitle>{currentNotification.title}</AlertTitle>
        )}
        {currentNotification.message}
      </Alert>
    </Snackbar>
  );
};

// Hook for easy notification usage
export const useNotify = () => {
  const success = (message: string, options?: Partial<Notification>) => 
    notificationService.success(message, options);
  
  const error = (error: any, options?: Partial<Notification>) => 
    notificationService.error(error, options);
  
  const warning = (message: string, options?: Partial<Notification>) => 
    notificationService.warning(message, options);
  
  const info = (message: string, options?: Partial<Notification>) => 
    notificationService.info(message, options);

  return { success, error, warning, info };
};